import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { buildBreedingCardHtml, printHtml, printedName, DEFAULT_SETTINGS } from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, Mail, Stethoscope, Send, Loader2, X, Save, Building2 } from "lucide-react";

const HEADER_FIELDS = [
  { key: "owner", label: "Owner", type: "text", placeholder: "e.g. Jane Doe" },
  { key: "last_service_date", label: "Last Date Serviced", type: "date" },
  { key: "confirmed_in_foal_date", label: "Confirmed in Foal Date", type: "date" },
  { key: "estimated_due_date", label: "Estimated Due Date", type: "date" },
  { key: "foal_out_date", label: "Foal Out Date", type: "date" },
];

const FOAL_FIELDS = [
  { key: "stallion", label: "Stallion", placeholder: "e.g. Justify" },
  { key: "sex", label: "Sex", placeholder: "Colt / Filly" },
  { key: "color", label: "Color", placeholder: "e.g. Bay" },
  { key: "breed", label: "Breed", placeholder: "e.g. Thoroughbred" },
];

const emptyFoal = {
  stallion: "",
  sex: "",
  color: "",
  breed: "",
  igg_result: "",
  igg_drawn_at: "",
  igg_recheck_result: "",
  igg_recheck_drawn_at: "",
};

// Average equine gestation ≈ 340 days.
const GESTATION_DAYS = 340;
const addDays = (isoDate, days) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function BreedingCardDialog({ open, onOpenChange, mare, year, settings = DEFAULT_SETTINGS, onSaved }) {
  const { readOnly, isVet, activeFarm } = useAuth();
  const [form, setForm] = useState({});
  const [foalForm, setFoalForm] = useState(emptyFoal);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'save' | 'print' | 'vet' | 'email'
  const [emailMode, setEmailMode] = useState(false);
  const [recipient, setRecipient] = useState("");

  const farmName = (settings?.farm_name || "").trim() || printedName(settings || DEFAULT_SETTINGS) || "";

  const load = useCallback(async () => {
    if (!mare) return;
    setLoading(true);
    setEmailMode(false);
    setRecipient("");
    setForm({
      owner: mare.owner || "",
      last_service_date: mare.last_service_date || "",
      confirmed_in_foal_date: mare.confirmed_in_foal_date || "",
      estimated_due_date: mare.estimated_due_date || "",
      foal_out_date: mare.foal_out_date || "",
    });
    try {
      const [hist, foals] = await Promise.all([
        api.get(`/mares/${mare.id}/records/year/${year}`),
        api.get("/foals"),
      ]);
      setHistory(hist.data);
      const existing = (foals.data || []).find(
        (f) => f.mare_id === mare.id && f.foal_out_date === (mare.foal_out_date || "")
      );
      setFoalForm(
        existing
          ? {
              stallion: existing.stallion || "",
              sex: existing.sex || "",
              color: existing.color || "",
              breed: existing.breed || "",
              igg_result: existing.igg_result || "",
              igg_drawn_at: existing.igg_drawn_at || "",
              igg_recheck_result: existing.igg_recheck_result || "",
              igg_recheck_drawn_at: existing.igg_recheck_drawn_at || "",
            }
          : { ...emptyFoal }
      );
    } catch {
      toast.error("Could not load card data");
    } finally {
      setLoading(false);
    }
  }, [mare, year]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Persist header fields to the mare + upsert a foal record when a Foal Out Date is set.
  const persist = async () => {
    const updated = { ...mare, ...form };
    if (readOnly) return updated;
    await api.put(`/mares/${mare.id}`, {
      owner: form.owner || "",
      last_service_date: form.last_service_date || "",
      confirmed_in_foal_date: form.confirmed_in_foal_date || "",
      estimated_due_date: form.estimated_due_date || "",
      foal_out_date: form.foal_out_date || "",
    });
    if ((form.foal_out_date || "").trim()) {
      await api.post("/foals", {
        mare_id: mare.id,
        foal_out_date: form.foal_out_date,
        mare_name: mare.name,
        farm: farmName,
        stallion: foalForm.stallion || "",
        sex: foalForm.sex || "",
        color: foalForm.color || "",
        breed: foalForm.breed || "",
        igg_result: foalForm.igg_result || "",
        igg_drawn_at: foalForm.igg_drawn_at || "",
        igg_recheck_result: foalForm.igg_recheck_result || "",
        igg_recheck_drawn_at: foalForm.igg_recheck_drawn_at || "",
      });
    }
    if (onSaved) onSaved(updated);
    return updated;
  };

  const buildHtml = (mareData) => buildBreedingCardHtml(mareData, history, settings);

  const handleSave = async () => {
    setBusy("save");
    try {
      await persist();
      toast.success("Changes saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(null);
    }
  };

  const handlePrint = async () => {
    if (!history) return;
    setBusy("print");
    try {
      const m = await persist();
      printHtml(buildHtml(m));
      toast.success("Breeding card sent to print");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async (to, kind) => {
    if (!to) {
      toast.error(
        kind === "vet"
          ? isVet
            ? "No contact email saved for this farm"
            : "No vet email saved in Settings"
          : "Enter a recipient email"
      );
      return;
    }
    if (!history) return;
    setBusy(kind);
    try {
      const m = await persist();
      const replyTo = (isVet ? activeFarm?.contact_email : settings?.owner_email) || "";
      await api.post("/breeding-card/email", {
        html: buildHtml(m),
        recipient: to,
        mare_name: mare.name,
        year,
        farm_name: farmName,
        reply_to: replyTo,
      });
      toast.success(`Breeding card emailed to ${to}`);
      setEmailMode(false);
      setRecipient("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally {
      setBusy(null);
    }
  };

  const vetEmail = settings?.vet_email || "";
  const farmRecipient = isVet ? (activeFarm?.contact_email || "") : vetEmail;
  const sendToLabel = isVet ? "Send to Farm" : "Send to Vet";
  const sendToTitle = isVet
    ? (farmRecipient ? `Send to ${farmRecipient}` : "Add a contact email on this farm")
    : (vetEmail ? `Send to ${vetEmail}` : "Set a vet email in Settings");
  const hasFoalDate = !!(form.foal_out_date || "").trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Breeding Card · {mare?.name}</DialogTitle>
          <DialogDescription>
            Review the {year} card details, then save, print or email it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 py-2">
              {HEADER_FIELDS.map((f) => (
                <div key={f.key} className={`space-y-1.5 ${f.key === "owner" ? "col-span-2" : ""}`}>
                  <Label htmlFor={`bc-${f.key}`}>{f.label}</Label>
                  <Input
                    id={`bc-${f.key}`}
                    type={f.type}
                    value={form[f.key] || ""}
                    placeholder={f.placeholder}
                    disabled={readOnly}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((s) => {
                        const next = { ...s, [f.key]: val };
                        if (f.key === "last_service_date") {
                          next.estimated_due_date = addDays(val, GESTATION_DAYS);
                        }
                        return next;
                      });
                    }}
                    data-testid={`bc-field-${f.key}`}
                  />
                  {f.key === "estimated_due_date" && form.last_service_date && (
                    <p className="text-[11px] text-muted-foreground">
                      Auto-calculated from Last Date Serviced (~340-day gestation) · editable
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Foal record — appears once a Foal Out Date is set */}
            {hasFoalDate && (
              <div className="rounded-xl border border-border bg-secondary/40 p-4 mt-1" data-testid="bc-foal-section">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-sm">Foal Record</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Auto-created for <strong>{mare?.name}</strong>{farmName ? ` · ${farmName}` : ""} · Foal out {form.foal_out_date}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {FOAL_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                      <Label htmlFor={`foal-${f.key}`}>{f.label}</Label>
                      {f.key === "sex" ? (
                        <Select
                          value={foalForm.sex || ""}
                          onValueChange={(v) => setFoalForm((s) => ({ ...s, sex: v }))}
                          disabled={readOnly}
                        >
                          <SelectTrigger id="foal-sex" data-testid="foal-field-sex">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Colt">Colt</SelectItem>
                            <SelectItem value="Filly">Filly</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={`foal-${f.key}`}
                          value={foalForm[f.key] || ""}
                          placeholder={f.placeholder}
                          disabled={readOnly}
                          onChange={(e) => setFoalForm((s) => ({ ...s, [f.key]: e.target.value }))}
                          data-testid={`foal-field-${f.key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-xs uppercase tracking-wide text-primary">Immunoglobulin (IgG)</h4>
                  </div>
                  <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">IgG Result</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="foal-igg_result">Result</Label>
                      <Input
                        id="foal-igg_result"
                        value={foalForm.igg_result || ""}
                        placeholder="e.g. > 800 mg/dL"
                        disabled={readOnly}
                        onChange={(e) => setFoalForm((s) => ({ ...s, igg_result: e.target.value }))}
                        data-testid="foal-field-igg_result"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="foal-igg_drawn_at">Date / Time of Draw</Label>
                      <Input
                        id="foal-igg_drawn_at"
                        type="datetime-local"
                        value={foalForm.igg_drawn_at || ""}
                        disabled={readOnly}
                        onChange={(e) => setFoalForm((s) => ({ ...s, igg_drawn_at: e.target.value }))}
                        data-testid="foal-field-igg_drawn_at"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">Recheck IgG</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="foal-igg_recheck_result">Result</Label>
                      <Input
                        id="foal-igg_recheck_result"
                        value={foalForm.igg_recheck_result || ""}
                        placeholder="e.g. > 800 mg/dL"
                        disabled={readOnly}
                        onChange={(e) => setFoalForm((s) => ({ ...s, igg_recheck_result: e.target.value }))}
                        data-testid="foal-field-igg_recheck_result"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="foal-igg_recheck_drawn_at">Date / Time of Draw</Label>
                      <Input
                        id="foal-igg_recheck_drawn_at"
                        type="datetime-local"
                        value={foalForm.igg_recheck_drawn_at || ""}
                        disabled={readOnly}
                        onChange={(e) => setFoalForm((s) => ({ ...s, igg_recheck_drawn_at: e.target.value }))}
                        data-testid="foal-field-igg_recheck_drawn_at"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {history && (
              <p className="text-xs text-muted-foreground mt-2">
                {history.total} record{history.total === 1 ? "" : "s"} in {year} · {history.filled} with entries
                {mare?.stallion ? ` · Stallion: ${mare.stallion}` : ""}
              </p>
            )}

            {emailMode ? (
              <div className="flex items-center gap-2 pt-2">
                <Input
                  type="email"
                  autoFocus
                  placeholder="recipient@email.com"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  data-testid="bc-email-input"
                />
                <Button onClick={() => sendEmail(recipient, "email")} disabled={busy === "email"} className="gap-1.5" data-testid="bc-email-send">
                  {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setEmailMode(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2 pt-3">
                {!readOnly && (
                  <Button onClick={handleSave} disabled={!!busy} className="gap-1.5" data-testid="bc-save-btn">
                    {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
                  </Button>
                )}
                <Button variant="outline" onClick={handlePrint} disabled={!history || !!busy} className="gap-1.5" data-testid="bc-print-btn">
                  {busy === "print" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Print
                </Button>
                <Button variant="outline" onClick={() => sendEmail(farmRecipient, "vet")} disabled={!history || !!busy} className="gap-1.5" data-testid="bc-vet-btn" title={sendToTitle}>
                  {busy === "vet" ? <Loader2 className="h-4 w-4 animate-spin" /> : isVet ? <Building2 className="h-4 w-4" /> : <Stethoscope className="h-4 w-4" />} {sendToLabel}
                </Button>
                <Button variant="outline" onClick={() => setEmailMode(true)} disabled={!history || !!busy} className="gap-1.5" data-testid="bc-email-btn">
                  <Mail className="h-4 w-4" /> Email
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
